import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUtilityDetail } from './hooks/useUtilityDetail';
import { cn } from '@/utils/cn';

export function UtilitySystemDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { system, components, isLoading } = useUtilityDetail(id || '');

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-32 rounded-md bg-white border border-slate-100" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
           <div className="h-24 rounded-md bg-white border border-slate-100" />
           <div className="h-24 rounded-md bg-white border border-slate-100" />
           <div className="h-24 rounded-md bg-white border border-slate-100" />
           <div className="h-24 rounded-md bg-white border border-slate-100" />
        </div>
      </div>
    );
  }

  if (!system) {
    return (
      <div className="py-20 text-center">
        <p className="sm:text-sm text-xs font-normal text-slate-500">Không tìm thấy hệ thống tiện ích</p>
        <button onClick={() => navigate('/utility/systems')} className="mt-4 text-emerald-600 font-normal hover:underline sm:text-sm text-xs">Quay lại danh sách</button>
      </div>
    );
  }

  const renderStatusBadge = (status: string) => {
    const configs: Record<string, { label: string; className: string }> = {
      operational: { label: 'Hoạt động', className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
      active: { label: 'Hoạt động', className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
      installing: { label: 'Đang lắp đặt', className: 'bg-blue-50 text-blue-600 border-blue-100' },
      testing: { label: 'Đang thử nghiệm', className: 'bg-amber-50 text-amber-600 border-amber-100' },
    };
    const config = configs[status] || { label: status, className: 'bg-slate-50 text-slate-500 border-slate-100' };
    return (
      <span className={cn("inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-normal  tracking-tight", config.className)}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="mx-auto max-w-screen-2xl space-y-4 pb-10">
      {/* Header Block */}
      <div className="rounded-md bg-white p-4 border border-slate-100 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-md bg-slate-900 text-white">
             <i className="ri-node-tree text-3xl" />
          </div>
          <div>
            <div className="flex items-center gap-2">
               <h2 className="text-xl font-normal text-slate-900">{system.system_name}</h2>
               {renderStatusBadge(system.status_code)}
            </div>
            <p className="sm:text-sm text-xs font-normal text-slate-500">{system.system_code} • {system.system_type.toUpperCase()}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
           <button className="h-10 rounded-md border border-slate-200 px-4 sm:text-sm text-xs font-normal text-slate-600 hover:bg-slate-50 transition-all">Sửa cấu hình</button>
           <button className="h-10 rounded-md bg-emerald-700 px-4 sm:text-sm text-xs font-normal text-white hover:bg-emerald-800 transition-all">Thử nghiệm hệ thống</button>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Dự án', value: system.project?.name, icon: 'ri-building-line', color: 'text-blue-600' },
          { label: 'Khu vực', value: system.zone?.name, icon: 'ri-map-pin-line', color: 'text-emerald-600' },
          { label: 'Đội thi công', value: system.team?.name, icon: 'ri-group-line', color: 'text-violet-600' },
          { label: 'Ngày bàn giao', value: system.tested_at ? new Date(system.tested_at).toLocaleDateString('vi-VN') : '---', icon: 'ri-calendar-check-line', color: 'text-amber-600' },
        ].map((item, i) => (
          <div key={i} className="rounded-md bg-white p-4 border border-slate-100 flex items-center gap-3">
             <div className={cn("text-xl", item.color)}><i className={item.icon} /></div>
             <div className="flex flex-col">
                <span className="text-[10px] font-normal text-slate-400 tracking-widest">{item.label}</span>
                <span className="sm:text-sm text-xs font-normal text-slate-700 truncate max-w-[150px]">{item.value || 'Chưa xác định'}</span>
             </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-12 space-y-4">
           {/* Description Block */}
           <div className="rounded-md bg-white p-4 border border-slate-100 space-y-4">
              <h3 className="sm:text-sm text-xs font-normal text-slate-400 tracking-widest border-b border-slate-50 pb-2">Mô tả hệ thống</h3>
              <p className="sm:text-sm text-xs text-slate-600 leading-relaxed">
                 {system.description || 'Không có mô tả chi tiết cho hệ thống này.'}
              </p>
           </div>

           {/* Components Table */}
           <div className="rounded-md bg-white border border-slate-100 overflow-hidden">
              <div className="p-4 border-b border-slate-50 flex items-center justify-between">
                 <h3 className="sm:text-sm text-xs font-normal text-slate-400 tracking-widest">Danh sách linh kiện / Thiết bị đầu cuối</h3>
                 <span className="rounded-full bg-slate-100 px-2.5 py-0.5 sm:text-sm text-xs font-normal text-slate-600">{components.length} linh kiện</span>
              </div>
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-slate-50 bg-slate-50/50">
                    <th className="p-4 text-[11px] font-normal text-slate-500 tracking-tight">Linh kiện</th>
                    <th className="p-4 text-[11px] font-normal text-slate-500 tracking-tight">Loại</th>
                    <th className="p-4 text-[11px] font-normal text-slate-500 tracking-tight">Số lượng</th>
                    <th className="p-4 text-[11px] font-normal text-slate-500 tracking-tight">Trạng thái</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {components.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="p-8 text-center text-slate-400 sm:text-sm text-xs italic">Chưa có linh kiện nào được đăng ký</td>
                    </tr>
                  ) : components.map((comp) => (
                    <tr key={comp.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="p-4">
                         <p className="sm:text-sm text-xs font-normal text-slate-900">{comp.name}</p>
                         <p className="text-[10px] font-normal text-slate-400">{comp.code}</p>
                      </td>
                      <td className="p-4 sm:text-sm text-xs font-normal text-slate-700">{comp.component_type}</td>
                      <td className="p-4 sm:text-sm text-xs font-normal text-slate-900">{comp.quantity} {comp.unit}</td>
                      <td className="p-4">
                         <span className={cn(
                           "inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-normal  tracking-tight",
                           comp.status_code === 'installed' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-slate-50 text-slate-500 border-slate-100'
                         )}>
                            {comp.status_code}
                         </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
           </div>
        </div>
      </div>
    </div>
  );
}

export default UtilitySystemDetailPage;
